import {
  LayoutGrid, Users, HardHat, Component, Wrench, History,
  Palette, Bell, Tv, ScrollText, UserShield, FileBox, Cable, ListTree, CalendarDays,
  DatabaseBackup,
} from 'lucide-react'

/**
 * How every glyph tile in the app is drawn, in one table: the glyph, and the two colours.
 *
 * `tint` is the saturated hue — the glyph itself, and the colour the thing is "known by".
 * `bg` is the wash the tile is filled with: chosen by hand rather than derived, because a
 * mechanical mix towards white pulls some hues grey and leaves others neon, and these
 * fourteen sit beside each other down one edge where that shows.
 *
 * The GLYPH lives here for the same reason the colours do. The same thing is drawn in more
 * than one place — a mode is a row in the settings sidebar, a card on Configure AND a card
 * on Overview — and each place had picked its own. Overview drew a calendar and a lightning
 * bolt for the two modes the sidebar drew as a hard hat and a component, so the two screens
 * disagreed about what the same mode looks like. Naming it once is the only way that stays
 * fixed.
 *
 * Deliberately NOT the tenant's brand: the brand already owns the selected row and the
 * primary buttons, and tinting these with it too would collapse the sidebar into one hue.
 * Configure Tenant is the single exception — it is the row that decides what the brand IS,
 * so it wears it.
 */
export const TILE = {
  /**
   * The board's own tile — on its page heading, and on the row that picks it in the nav.
   *
   * The tenant's own brand, which is the one colour in the app that means "this company",
   * and the only entry here besides Configure that wears it. That is the distinction worth
   * drawing: a list picks a colour for itself out of a palette, and the board is not one of
   * them — it is the thing the company runs on, so it wears the company's colour rather
   * than a colour it chose.
   *
   * `bg` is the brand's own derived wash rather than a hex approximating it (the trick
   * TILE.configure uses), so the tile is exactly right for every tenant — see
   * applyBranding in lib/theme.js, which sets both of these at runtime.
   */
  board: { icon: CalendarDays, tint: 'var(--brand-primary)', bg: 'var(--brand-tint)' },

  overview: { icon: LayoutGrid, tint: '#5e5ce6', bg: '#e8e7ff' },
  people: { icon: Users, tint: '#30b0c7', bg: '#e3f6f8' },

  // The two operating modes. Read by the settings sidebar, the mode cards on Configure and
  // the switcher on Overview — which is exactly the drift this table exists to stop.
  // Group mode is the whole company reorganised into interchangeable crews, which is what
  // lucide's "component" glyph reads as: parts arranged around a centre.
  modeNormal: { icon: HardHat, tint: '#006400', bg: '#e7f5e8' },
  modeCat: { icon: Component, tint: '#800000', bg: '#f9e7e7' },

  jobTypes: { icon: Wrench, tint: '#ff9500', bg: '#fff0d9' },
  modeHistory: { icon: History, tint: '#a2845e', bg: '#f0e9de' },

  // The register, and the switch that decides whether the board draws a band for it. One
  // look for both: they are the same feature seen from two places, and giving the switch its
  // own colour and its own glyph only invited the question of what the difference was.
  assets: { icon: FileBox, tint: '#5856d6', bg: '#e9e7fa' },

  // Lists, and the switch that turns them on — one look for both, as with the assets pair
  // above. Each list then wears its OWN colour and glyph on its board; this is the look of
  // the feature, not of any one list.
  lists: { icon: ListTree, tint: '#0f766e', bg: '#dcf2ee' },

  configure: { icon: UserShield, tint: 'var(--brand-primary)', bg: '#e4eeff' },
  branding: { icon: Palette, tint: '#ff375f', bg: '#ffe7ee' },
  notifications: { icon: Bell, tint: '#ff9f0a', bg: '#fff0d6' },
  displays: { icon: Tv, tint: '#af52de', bg: '#f0e5fa' },
  integrations: { icon: Cable, tint: '#d97757', bg: '#f0e7e2' },
  auditLog: { icon: ScrollText, tint: '#48484a', bg: '#e9e9eb' },
  // Backups: the company's data as a thing you can hold a copy of. A steel blue, beside the
  // audit log's grey — the two rows at the bottom are the record-keeping pair.
  data: { icon: DatabaseBackup, tint: '#3d5a80', bg: '#e6ecf3' },
}

/** The look for an operating mode, keyed by the identifier that is actually stored. */
export const modeTile = (mode) => (mode === 'cat' ? TILE.modeCat : TILE.modeNormal)
